import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Bell, Heart, MessageCircle, UserPlus, Repeat2, CheckCheck,
  Coins, AtSign, AlertCircle,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const iconMap = {
  like:         <Heart className="w-5 h-5 text-red-500 fill-red-500" />,
  comment:      <MessageCircle className="w-5 h-5 text-blue-500" />,
  follow:       <UserPlus className="w-5 h-5 text-green-500" />,
  repost:       <Repeat2 className="w-5 h-5 text-purple-500" />,
  tip:          <Coins className="w-5 h-5 text-yellow-500" />,
  donation:     <Coins className="w-5 h-5 text-yellow-500" />,
  message:      <MessageCircle className="w-5 h-5 text-primary" />,
  mention:      <AtSign className="w-5 h-5 text-purple-400" />,
  subscription: <Coins className="w-5 h-5 text-yellow-500" />,
  system:       <AlertCircle className="w-5 h-5 text-orange-500" />,
};

function getNotificationLink(n) {
  const username = n.from_user?.username;
  switch (n.type) {
    case 'follow':  return username ? `/profile/${username}` : '/notifications';
    case 'like':
    case 'comment':
    case 'repost':
    case 'mention': return n.post_id ? `/post/${n.post_id}` : '/notifications';
    case 'tip':
    case 'donation': return '/wallet';
    case 'message': return username ? `/messages?user=${n.from_user_id}` : '/messages';
    default:        return '/notifications';
  }
}

export default function NotificationsPage() {
  const { notifications, unreadCount, markRead, markAllRead, refresh } = useNotifications();
  const navigate = useNavigate();

  // Refresh on mount so we have latest data
  useEffect(() => { refresh(); }, [refresh]);

  const loading = notifications === null;

  const handleNotificationClick = async (n) => {
    if (!n.is_read) await markRead(n.id);
    navigate(getNotificationLink(n));
  };

  return (
    <div className="mg-page max-w-2xl" data-testid="notifications-page">
      {/* Header */}
      <div className="py-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Bell className="w-6 h-6 text-primary" />
            Notifications
          </h1>
          {unreadCount > 0 && (
            <p className="text-sm text-muted-foreground mt-1">
              {unreadCount} unread notification{unreadCount > 1 ? 's' : ''}
            </p>
          )}
        </div>
        
        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={markAllRead}
            className="mg-btn mg-btn-secondary text-xs rounded-lg"
            data-testid="mark-all-read-btn"
          >
            <CheckCheck className="w-4 h-4 mr-2" />
            Mark all read
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <div className="space-y-2 pb-8" data-testid="notifications-list">
        {loading ? (
          Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-start gap-4 p-4 rounded-xl">
              <Skeleton className="w-12 h-12 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-full max-w-xs" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          ))
        ) : notifications.length === 0 ? (
          <div className="text-center py-16" data-testid="notifications-empty">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Bell className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-heading text-lg font-bold mb-2">No notifications yet</h3>
            <p className="text-muted-foreground">
              When people interact with your content, you'll see it here.
            </p>
          </div>
        ) : (
          notifications.map((notification) => (
            <button
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`w-full flex items-start gap-4 p-4 rounded-xl transition-colors text-left ${
                notification.is_read 
                  ? 'hover:bg-muted/50' 
                  : 'bg-primary/5 hover:bg-primary/10'
              }`}
              data-testid={`notification-${notification.id}`}
            >
              {/* User Avatar with Icon */}
              <div className="relative">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={notification.from_user?.avatar} />
                  <AvatarFallback>{notification.from_user?.display_name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-card flex items-center justify-center">
                  {iconMap[notification.type] || <Bell className="w-5 h-5 text-muted-foreground" />}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-sm">
                  {notification.content}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                </p>
              </div>

              {/* Unread Indicator */}
              {!notification.is_read && (
                <div className="w-2.5 h-2.5 rounded-full bg-primary mt-2 shrink-0" data-testid="unread-dot" />
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
